package org.dronda.app.web.ui.wishlist

import androidx.compose.runtime.Stable
import com.github.michaelbull.result.onErr
import com.github.michaelbull.result.onOk
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import org.dronda.api.wishlist.AddWishlistItem
import org.dronda.api.wishlist.EditWishlistItem
import org.dronda.api.wishlist.WishListFull
import org.dronda.app.web.repository.WishlistRepository

class WishlistViewModel(
    private val wishlistId: String,
    val currentUserId: String,
    private val wishlistRepository: WishlistRepository
) {
    private val scope = CoroutineScope(Dispatchers.Default + SupervisorJob())
    private val _uiState = MutableStateFlow<UiState>(UiState.Loading)
    val uiState: StateFlow<UiState> = _uiState.asStateFlow()

    private val _dialogState = MutableStateFlow<DialogState>(DialogState.Hidden)
    val dialogState: StateFlow<DialogState> = _dialogState.asStateFlow()

    init {
        loadWishlist()
    }

    fun take(event: ViewEvent) {
        when (event) {
            ViewEvent.Refresh -> loadWishlist()
            ViewEvent.ShowAddItemDialog -> _dialogState.value = DialogState.Visible()
            ViewEvent.HideAddItemDialog -> _dialogState.value = DialogState.Hidden
            is ViewEvent.AddItem -> addItem(event.name, event.description, event.url)
            is ViewEvent.ShowEditItemDialog -> {
                _dialogState.value = DialogState.Visible(
                    isEditing = true,
                    editingItemId = event.itemId,
                    name = event.name,
                    description = event.description ?: "",
                    url = event.url ?: ""
                )
            }
            is ViewEvent.UpdateItem -> updateItem(
                event.itemId,
                event.name,
                event.description,
                event.url
            )
            is ViewEvent.DeleteItem -> deleteItem(event.itemId)
            is ViewEvent.TogglePurchaseStatus -> togglePurchaseStatus(event.itemId)
        }
    }

    private fun loadWishlist() {
        scope.launch {
            _uiState.value = UiState.Loading
            wishlistRepository.getWishlist(wishlistId)
                .onOk { wishlist ->
                    _uiState.value = UiState.Success(wishlist)
                }
                .onErr {
                    _uiState.value = UiState.Error("Failed to load wishlist")
                }
        }
    }

    private fun addItem(name: String, description: String?, url: String?) {
        scope.launch {
            _dialogState.value = DialogState.Submitting
            wishlistRepository.addItem(
                wishlistId,
                AddWishlistItem(name, description, url)
            )
                .onOk {
                    _dialogState.value = DialogState.Hidden
                    loadWishlist()
                }
                .onErr {
                    _dialogState.value = DialogState.Error("Failed to add item")
                }
        }
    }

    private fun updateItem(itemId: String, name: String, description: String?, url: String?) {
        scope.launch {
            _dialogState.value = DialogState.Submitting
            wishlistRepository.updateItem(
                wishlistId,
                itemId,
                EditWishlistItem(name, description, url)
            )
                .onOk { updatedWishlist ->
                    _dialogState.value = DialogState.Hidden
                    _uiState.value = UiState.Success(updatedWishlist)
                }
                .onErr {
                    _dialogState.value = DialogState.Error("Failed to update item")
                }
        }
    }

    private fun deleteItem(itemId: String) {
        scope.launch {
            wishlistRepository.deleteItem(wishlistId, itemId)
                .onOk {
                    loadWishlist()
                }
                .onErr {
                    // TODO: Show error to user
                }
        }
    }

    private fun togglePurchaseStatus(itemId: String) {
        scope.launch {
            println(uiState.value)
            val uiState = uiState.value as? UiState.Success ?: return@launch
            val item = uiState.wishlist.wishlistItems.find { it.id == itemId } ?: return@launch
            println(item)
            if (item.isPurchased && item.checkedUser?.id != currentUserId) {
                return@launch
            }
            wishlistRepository.toggleItemPurchaseStatus(wishlistId, itemId)
                .onOk { updatedWishlist ->
                    _uiState.value = UiState.Success(updatedWishlist)
                }
                .onErr {
                    console.error("Failed to toggle purchase status")
                }
        }
    }

    @Stable
    sealed interface UiState {
        data object Loading : UiState
        data class Success(val wishlist: WishListFull) : UiState
        data class Error(val message: String) : UiState
    }

    sealed interface DialogState {
        data object Hidden : DialogState
        data class Visible(
            val isEditing: Boolean = false,
            val editingItemId: String? = null,
            val name: String = "",
            val description: String = "",
            val url: String = ""
        ) : DialogState
        data object Submitting : DialogState
        data class Error(val message: String) : DialogState
    }

    sealed interface ViewEvent {
        data object Refresh : ViewEvent
        data object ShowAddItemDialog : ViewEvent
        data object HideAddItemDialog : ViewEvent
        data class AddItem(val name: String, val description: String?, val url: String?) : ViewEvent
        data class ShowEditItemDialog(
            val itemId: String,
            val name: String,
            val description: String?,
            val url: String?
        ) : ViewEvent
        data class UpdateItem(
            val itemId: String,
            val name: String,
            val description: String?,
            val url: String?
        ) : ViewEvent
        data class DeleteItem(val itemId: String) : ViewEvent
        data class TogglePurchaseStatus(val itemId: String) : ViewEvent
    }
}
